// src/components/Header.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Menu, X, User, LogOut } from 'lucide-react';
import useAuthStore from '../store/authStore';

const Header = () => {
  const { isLoggedIn, user, logout } = useAuthStore();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0); // شمارنده محلی برای هدر

  // به‌روزرسانی شمارنده سبد خرید
  const updateCartCount = () => {
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    setCartCount(cart.length); // یا cart.reduce((sum, item) => sum + (item.quantity || 1), 0)
  };

  useEffect(() => {
    updateCartCount(); // بار اول

    // گوش دادن به رویداد cartUpdated (که در ProductDetail.js dispatch می‌شه)
    window.addEventListener('cartUpdated', updateCartCount);

    // همچنین وقتی localStorage تغییر کرد (از تب دیگر یا غیره)
    window.addEventListener('storage', updateCartCount);

    return () => {
      window.removeEventListener('cartUpdated', updateCartCount);
      window.removeEventListener('storage', updateCartCount);
    };
  }, []);

  const menuItems = [
    { name: 'خانه', path: '/' },
    { name: 'محصولات', path: '/products' },
    { name: 'محصولات اقتصادی', path: '/affordable' },
    ...(isLoggedIn ? [{ name: 'صورتحساب‌ها', path: '/profile/orders' }] : []),
    { name: 'درباره ما', path: '/about' },
    { name: 'تماس با ما', path: '/contact' },
  ];

  return (
    <header className="bg-white shadow-md sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16 lg:h-20">
          {/* لوگو */}
          <Link to="/" className="flex items-center">
            <div className="text-2xl lg:text-3xl font-bold text-gold">
              خرمی‌گلد
            </div>
          </Link>

          {/* منوی دسکتاپ */}
          <nav className="hidden lg:flex items-center space-x-8 space-x-reverse">
            {menuItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className="text-gray-700 hover:text-gold transition duration-300 font-medium"
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* بخش راست: سبد خرید و وضعیت کاربر */}
          <div className="flex items-center space-x-4 space-x-reverse">
            {/* سبد خرید با شمارنده پویا */}
            <Link to="/cart" className="relative p-2 hover:bg-gold-light rounded-full transition">
              <ShoppingCart className="w-6 h-6 text-gray-700" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>

            {/* وضعیت کاربر */}
            {isLoggedIn && user ? (
              <div className="flex items-center space-x-4 space-x-reverse">
                <Link
                  to="/profile"
                  className="flex items-center gap-2 text-gray-700 hover:text-gold transition font-medium"
                >
                  <User className="w-6 h-6" />
                  <span className="hidden md:block">
                    {user.first_name || 'پروفایل'}
                  </span>
                </Link>

                <button
                  onClick={logout}
                  className="flex items-center gap-2 text-red-600 hover:text-red-800 transition"
                  title="خروج"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="hidden md:block text-sm">خروج</span>
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="bg-gold hover:bg-gold-dark text-white px-6 py-3 rounded-lg transition font-semibold shadow-md"
              >
                ورود / عضویت
              </Link>
            )}

            {/* دکمه منو موبایل */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2"
            >
              {isMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
            </button>
          </div>
        </div>

        {/* منوی موبایل */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200">
            <nav className="flex flex-col space-y-4">
              {menuItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  className="text-gray-700 hover:text-gold text-lg font-medium py-2"
                >
                  {item.name}
                </Link>
              ))}

              {/* وضعیت کاربر در موبایل */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                {isLoggedIn && user ? (
                  <div className="space-y-3">
                    <Link
                      to="/profile"
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center gap-3 text-gray-700 py-2"
                    >
                      <User className="w-5 h-5" />
                      {user.first_name || 'پروفایل'}
                    </Link>

                    <Link
                      to="/profile/orders"
                      onClick={() => setIsMenuOpen(false)}
                      className="block text-gray-700 hover:text-gold py-2"
                    >
                      صورتحساب‌ها
                    </Link>

                    <button
                      onClick={() => {
                        logout();
                        setIsMenuOpen(false);
                      }}
                      className="flex items-center gap-3 text-red-600 w-full text-right py-2"
                    >
                      <LogOut className="w-5 h-5" />
                      خروج
                    </button>
                  </div>
                ) : (
                  <Link
                    to="/login"
                    onClick={() => setIsMenuOpen(false)}
                    className="block bg-gold hover:bg-gold-dark text-white py-3 rounded-lg text-center font-medium"
                  >
                    ورود / عضویت
                  </Link>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;